import { jsPDF } from 'jspdf';

const OL_GREEN = [0, 117, 102];
const GRAY = [120, 120, 120];
const LIGHT_GRAY = [180, 180, 180];
const BLACK = [0, 0, 0];
const LINE_COLOR = [180, 180, 180];
const CELL_BG = [248, 248, 248];

function formatDateShort(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) {
    if (typeof dateStr === 'string' && dateStr.includes('-')) {
      const [y, m, day] = dateStr.split('-');
      return `${parseInt(day)}-${parseInt(m)}-${y}`;
    }
    return dateStr;
  }
  return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
}

// ============================================================
// PALLELABEL (A6 landscape) — one page per pallet
// ============================================================
export function generatePalleLabel(booking) {
  const lines = (booking.lines && booking.lines.length > 0)
    ? booking.lines
    : [{ quantity: booking.quantity || 1, unit: booking.unit || 'Læs', goods_description: booking.goods_description || '', temperature_requirement: booking.temperature_requirement || booking.temperatureRequirement || '' }];

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [148, 105] });
  const w = 148;
  const h = 105;
  const margin = 6;

  let pageNum = 0;
  lines.forEach((line, lineIdx) => {
    const lineQty = line.quantity || 1;
    for (let p = 0; p < lineQty; p++) {
      pageNum++;
      if (pageNum > 1) doc.addPage([148, 105], 'landscape');

      // Border
      doc.setDrawColor(...LINE_COLOR);
      doc.setLineWidth(0.5);
      doc.rect(2, 2, w - 4, h - 4);

      // Header line
      doc.setDrawColor(...OL_GREEN);
      doc.setLineWidth(1.5);
      doc.line(margin, 18, w - margin, 18);

      // Company name
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...OL_GREEN);
      doc.text('Ole Larsen Transport A/S', margin, 12);

      // CMR number top-right
      const cmr = booking.cmr_number || booking.booking_reference || booking.reference || '';
      doc.setFontSize(10);
      doc.setTextColor(...BLACK);
      doc.setFont('helvetica', 'bold');
      doc.text(cmr, w - margin, 12, { align: 'right' });

      // Pallet count — prominent
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...OL_GREEN);
      doc.text(`Palle ${p + 1} af ${lineQty}`, w / 2, 24, { align: 'center' });

      // Divider
      doc.setDrawColor(...LINE_COLOR);
      doc.setLineWidth(0.3);
      doc.line(margin, 28, w - margin, 28);

      // Afsender / Modtager
      const leftCol = margin;
      const rightCol = w / 2 + 4;
      let yPos = 33;

      doc.setFontSize(7);
      doc.setTextColor(...GRAY);
      doc.text('AFSENDER', leftCol, yPos);
      doc.text('MODTAGER', rightCol, yPos);

      yPos += 5;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BLACK);
      doc.text(booking.pickup_name || booking.sender_name || '', leftCol, yPos, { maxWidth: 64 });
      doc.text(booking.delivery_name || booking.recipient_name || '', rightCol, yPos, { maxWidth: 64 });

      yPos += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...GRAY);
      doc.text(booking.pickup_address || '', leftCol, yPos, { maxWidth: 64 });
      doc.text(booking.delivery_address || '', rightCol, yPos, { maxWidth: 64 });

      yPos += 4;
      doc.text(`DK-${booking.pickup_postal_code || ''} ${booking.pickup_city || ''}`, leftCol, yPos);
      doc.text(`DK-${booking.delivery_postal_code || ''} ${booking.delivery_city || ''}`, rightCol, yPos);

      // Vertical divider
      doc.setDrawColor(...LINE_COLOR);
      doc.line(w / 2, 28, w / 2, 52);

      // Bottom section divider
      doc.line(margin, 56, w - margin, 56);

      yPos = 62;
      doc.setFontSize(7);
      doc.setTextColor(...GRAY);
      doc.text('DATO', leftCol, yPos);
      doc.text('INDHOLD', leftCol + 30, yPos);
      doc.text('TEMPERATUR', rightCol, yPos);

      yPos += 5;
      doc.setFontSize(11);
      doc.setTextColor(...BLACK);
      doc.setFont('helvetica', 'bold');
      const dateStr = booking.pickup_date ? formatDateShort(booking.pickup_date) : '';
      doc.text(dateStr, leftCol, yPos);
      doc.setFontSize(9);
      doc.text(line.goods_description || `${line.unit || 'Læs'}`, leftCol + 30, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(line.temperature_requirement || '-', rightCol, yPos);

      // CMR number large at bottom
      yPos = 82;
      doc.setFontSize(7);
      doc.setTextColor(...GRAY);
      doc.text('FRAGTBREV NR.', leftCol, yPos);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BLACK);
      doc.text(cmr, leftCol, yPos + 8);

      // Footer stripe
      doc.setFillColor(...OL_GREEN);
      doc.rect(0, h - 6, w, 6, 'F');
      doc.setFontSize(6);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'normal');
      doc.text('Ole Larsen Transport A/S  |  Dalgårdsvej 7, 8220 Brabrand  |  87 47 60 40  |  www.olelarsen.dk', w / 2, h - 2, { align: 'center' });
    }
  });

  return doc;
}

// ============================================================
// CMR / FRAGTBREV - matcher Ole Larsen original
// ============================================================
export function generateCMR(booking) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const w = 210;
  const margin = 15;
  const rightEdge = w - margin;
  const contentW = rightEdge - margin;

  const cmr = booking.cmr_number || '';
  const bookingId = booking.booking_id || '';
  const bookingSeq = bookingId ? `${bookingId}/1` : '';

  // ──────────────────────────────────────────
  // HEADER
  // ──────────────────────────────────────────

  // "Ole Larsen" logo text - italic
  doc.setFontSize(24);
  doc.setFont('times', 'bolditalic');
  doc.setTextColor(...OL_GREEN);
  doc.text('Ole Larsen', margin, 18);

  // "Fragtbrev" title top right
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BLACK);
  doc.text('Fragtbrev', rightEdge, 18, { align: 'right' });

  // Company info left
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY);
  doc.text('Ole Larsen Transport A/S', margin, 25);
  doc.text('Dalgårdsvej 7', margin, 29);
  doc.text('DK-8220 Brabrand', margin, 33);

  doc.text('Telefon: 8747 6040', margin + 48, 25);
  doc.text('Mail: booking@olelarsen.dk', margin + 48, 29);
  doc.text('Web: www.olelarsen.dk', margin + 48, 33);

  // Tur / CMR right-aligned under "Fragtbrev"
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GRAY);
  doc.text('Tur nr:', rightEdge - 30, 25, { align: 'right' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BLACK);
  doc.text(cmr, rightEdge, 25, { align: 'right' });

  // Barcode
  if (cmr) {
    drawBarcode(doc, cmr, rightEdge - 50, 28, 50, 8);
  }

  // Booking ID under barcode
  if (bookingSeq) {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GRAY);
    doc.text(bookingSeq, rightEdge, 42, { align: 'right' });
  }

  // Green line under header
  doc.setDrawColor(...OL_GREEN);
  doc.setLineWidth(1);
  doc.line(margin, 46, rightEdge, 46);

  // ──────────────────────────────────────────
  // CUSTOMER LINE
  // ──────────────────────────────────────────
  let y = 52;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BLACK);
  doc.text(booking.customer_name || '', margin, y);
  doc.text(booking.booking_reference || booking.reference || '', rightEdge, y, { align: 'right' });

  // ──────────────────────────────────────────
  // AFSENDER / MODTAGER BOXES
  // ──────────────────────────────────────────
  y = 57;
  const boxH = 30;
  const halfW = (contentW - 4) / 2;

  doc.setDrawColor(...LINE_COLOR);
  doc.setLineWidth(0.4);
  doc.rect(margin, y, halfW, boxH);
  doc.rect(margin + halfW + 4, y, halfW, boxH);

  // Afsender
  doc.setFontSize(7);
  doc.setTextColor(...GRAY);
  doc.text('Afsender', margin + 3, y + 5);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BLACK);
  doc.text(booking.pickup_name || booking.sender_name || '', margin + 3, y + 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(booking.pickup_address || '', margin + 3, y + 18);
  doc.text(
    `DK-${booking.pickup_postal_code || ''} ${booking.pickup_city || ''}`,
    margin + 3, y + 23
  );

  // Modtager
  const rx = margin + halfW + 7;
  doc.setFontSize(7);
  doc.setTextColor(...GRAY);
  doc.text('Modtager', rx, y + 5);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...BLACK);
  doc.text(booking.delivery_name || booking.recipient_name || '', rx, y + 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(booking.delivery_address || '', rx, y + 18);
  doc.text(
    `DK-${booking.delivery_postal_code || ''} ${booking.delivery_city || ''}`,
    rx, y + 23
  );

  // ──────────────────────────────────────────
  // DATO / REFERENCE ROW (6 columns)
  // ──────────────────────────────────────────
  y = 91;
  const rowH = 16;
  const cols = [
    { label: 'Afs. ref. nr.', w: contentW * 0.16 },
    { label: 'PO Nr.', w: contentW * 0.12 },
    { label: 'Dato for afh.', w: contentW * 0.14 },
    { label: 'Fra kl.', w: contentW * 0.10 },
    { label: 'Dato for lev.', w: contentW * 0.14 },
    { label: 'Inden kl.', w: contentW * 0.14 },
    { label: 'Temp.', w: contentW * 0.20 },
  ];

  doc.setDrawColor(...LINE_COLOR);
  doc.setLineWidth(0.4);
  doc.rect(margin, y, contentW, rowH);

  let cx = margin;
  for (let i = 0; i < cols.length; i++) {
    if (i > 0) {
      doc.line(cx, y, cx, y + rowH);
    }
    // Header
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...GRAY);
    doc.text(cols[i].label, cx + 2, y + 4);
    cx += cols[i].w;
  }

  // Values
  const vals = [
    booking.booking_reference || booking.reference || '',
    booking.po_number || booking.poNumber || '',
    booking.pickup_date ? formatDateShort(booking.pickup_date) : '',
    booking.pickup_time || '',
    booking.delivery_date ? formatDateShort(booking.delivery_date) : '',
    booking.delivery_time || '',
    booking.temperature_requirement || booking.temperatureRequirement || '',
  ];

  cx = margin;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BLACK);
  for (let i = 0; i < cols.length; i++) {
    doc.text(vals[i], cx + 2, y + 11);
    cx += cols[i].w;
  }

  // ──────────────────────────────────────────
  // BEMÆRKNINGER
  // ──────────────────────────────────────────
  y = 111;
  doc.setDrawColor(...LINE_COLOR);
  doc.rect(margin, y, contentW, 18);
  doc.setFontSize(7);
  doc.setTextColor(...GRAY);
  doc.text('Bemærkninger:', margin + 2, y + 5);

  doc.setFontSize(9);
  doc.setTextColor(...BLACK);
  const notes = [booking.pickup_notes, booking.delivery_notes].filter(Boolean).join(' | ');
  if (notes) {
    doc.text(notes, margin + 2, y + 11, { maxWidth: contentW - 4 });
  }

  // ──────────────────────────────────────────
  // VARELINIER
  // ──────────────────────────────────────────
  y = 133;
  doc.setDrawColor(...LINE_COLOR);
  doc.rect(margin, y, contentW, 8);
  doc.setFontSize(7);
  doc.setTextColor(...GRAY);
  doc.text('Varelinier:', margin + 2, y + 5);

  y = 141;
  const lines = (booking.lines && booking.lines.length > 0)
    ? booking.lines
    : [{ goods_description: booking.goods_description || booking.goodsType || '-', quantity: booking.quantity || 1, unit: booking.unit || 'Læs', temperature_requirement: booking.temperature_requirement || booking.temperatureRequirement || '' }];

  // Column positions (percentage of contentW)
  const lineRowH = 6;
  const colX = {
    desc: margin + 4,
    qty: margin + contentW * 0.34,
    unit: margin + contentW * 0.42,
    eu: margin + contentW * 0.52,
    half: margin + contentW * 0.62,
    quarter: margin + contentW * 0.72,
    temp: margin + contentW * 0.82,
  };

  // Column headers
  doc.setFillColor(...CELL_BG);
  doc.rect(margin, y, contentW, lineRowH, 'FD');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...GRAY);
  doc.text('Beskrivelse', colX.desc, y + 4);
  doc.text('Antal', colX.qty, y + 4);
  doc.text('Enhed', colX.unit, y + 4);
  doc.text('EU pll', colX.eu, y + 4);
  doc.text('½ pll', colX.half, y + 4);
  doc.text('¼ pll', colX.quarter, y + 4);
  doc.text('Temp.', colX.temp, y + 4);
  y += lineRowH;

  // Line rows
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  lines.forEach((line) => {
    doc.setDrawColor(...LINE_COLOR);
    doc.rect(margin, y, contentW, lineRowH);
    doc.setTextColor(...BLACK);
    doc.text(line.goods_description || '-', colX.desc, y + 4, { maxWidth: contentW * 0.30 });
    doc.text(String(line.quantity || 1), colX.qty, y + 4);
    doc.text(line.unit || 'Læs', colX.unit, y + 4);
    doc.text(String(line.pallet_eu || 0), colX.eu, y + 4);
    doc.text(String(line.pallet_half || 0), colX.half, y + 4);
    doc.text(String(line.pallet_quarter || 0), colX.quarter, y + 4);
    doc.setTextColor(...OL_GREEN);
    doc.text(line.temperature_requirement || '', colX.temp, y + 4);
    doc.setTextColor(...BLACK);
    y += lineRowH;
  });

  // Total row
  doc.setDrawColor(...LINE_COLOR);
  doc.rect(margin, y, contentW, lineRowH);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('Total', colX.desc, y + 4);
  const totalQty = lines.reduce((sum, l) => sum + (l.quantity || 1), 0);
  doc.text(String(totalQty), colX.qty, y + 4);
  const totalEu = lines.reduce((sum, l) => sum + (l.pallet_eu || 0), 0);
  const totalHalf = lines.reduce((sum, l) => sum + (l.pallet_half || 0), 0);
  const totalQuarter = lines.reduce((sum, l) => sum + (l.pallet_quarter || 0), 0);
  doc.text(String(totalEu), colX.eu, y + 4);
  doc.text(String(totalHalf), colX.half, y + 4);
  doc.text(String(totalQuarter), colX.quarter, y + 4);
  y += lineRowH + 4;

  // Sum delivered pallet counts across all lines
  const deliveredPallets = { eu: 0, half: 0, quarter: 0 };
  const returnPallets = { eu: 0, half: 0, quarter: 0 };

  lines.forEach(line => {
    deliveredPallets.eu += (line.pallet_eu || 0);
    deliveredPallets.half += (line.pallet_half || 0);
    deliveredPallets.quarter += (line.pallet_quarter || 0);
    returnPallets.eu += (line.return_pallet_eu || 0);
    returnPallets.half += (line.return_pallet_half || 0);
    returnPallets.quarter += (line.return_pallet_quarter || 0);
  });

  // Fallback to booking-level if lines have no pallet data
  if (deliveredPallets.eu + deliveredPallets.half + deliveredPallets.quarter === 0) {
    deliveredPallets.eu = booking.pallet_eu || 0;
    deliveredPallets.half = booking.pallet_half || 0;
    deliveredPallets.quarter = booking.pallet_quarter || 0;
  }

  // ──────────────────────────────────────────
  // AFSENDERS PALLEKVITTERING
  // ──────────────────────────────────────────
  drawPalletSection(doc, y, margin, contentW, 'Afsenders pallekvittering',
    ['Afhentet hos afsender', 'Emballage afleveret hos afsender'],
    'Afsenders underskrift:', deliveredPallets);

  // ──────────────────────────────────────────
  // MODTAGERS PALLEKVITTERING
  // ──────────────────────────────────────────
  y += 48;
  drawPalletSection(doc, y, margin, contentW, 'Modtagers pallekvittering',
    ['Leveret hos modtager', 'Retur emballage medtaget'],
    'Modtagers underskrift:', deliveredPallets, returnPallets);

  // ──────────────────────────────────────────
  // FOOTER
  // ──────────────────────────────────────────
  y += 48 + 4;
  doc.setFontSize(6);
  doc.setTextColor(...GRAY);
  doc.text(
    'Alle opgaver udføres i henhold til CMR-loven. All services rendered are subject to the general CMR-conditions. Se i øvrigt www.olelarsen.dk.',
    margin, y
  );

  // Vogn / Trailer / Chauffør
  y = 271;
  doc.setDrawColor(...LINE_COLOR);
  doc.setLineWidth(0.4);
  doc.rect(margin, y, contentW, 12);

  // Dividers inside
  const vCol1 = margin + contentW * 0.22;
  const vCol2 = margin + contentW * 0.33;
  const vCol3 = margin + contentW * 0.53;
  const vCol4 = margin + contentW * 0.63;
  const vCol5 = margin + contentW * 0.73;

  doc.line(vCol1, y, vCol1, y + 12);
  doc.line(vCol2, y, vCol2, y + 12);
  doc.line(vCol3, y, vCol3, y + 12);
  doc.line(vCol4, y, vCol4, y + 12);
  doc.line(vCol5, y, vCol5, y + 12);

  doc.setFontSize(7);
  doc.setTextColor(...GRAY);
  doc.text('Vogn nr:', margin + 2, y + 5);
  doc.text('/', (vCol1 + vCol2) / 2, y + 8, { align: 'center' });
  doc.text('Trailer nr:', vCol2 + 2, y + 5);
  doc.text('/', (vCol3 + vCol4) / 2, y + 8, { align: 'center' });
  doc.text('Chauffør:', vCol5 + 2, y + 5);

  return doc;
}

// ──────────────────────────────────────────
// PALLET SECTION
// ──────────────────────────────────────────
function drawPalletSection(doc, y, margin, contentW, title, rowLabels, signatureLabel, palletCounts, returnCounts) {
  doc.setDrawColor(...LINE_COLOR);
  doc.setLineWidth(0.3);

  // Title
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BLACK);
  doc.text(title, margin, y);

  const tableY = y + 3;
  const labelW = 42;
  const palletColW = 16;
  const tableW = labelW + palletColW * 3;
  const rowH = 7;

  // Header row
  doc.setFillColor(...CELL_BG);
  doc.rect(margin + labelW, tableY, palletColW * 3, rowH, 'FD');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...GRAY);
  doc.text('EU', margin + labelW + palletColW * 0.5, tableY + 5, { align: 'center' });
  doc.text('½', margin + labelW + palletColW * 1.5, tableY + 5, { align: 'center' });
  doc.text('1/4', margin + labelW + palletColW * 2.5, tableY + 5, { align: 'center' });

  // Column dividers
  for (let i = 0; i < 3; i++) {
    doc.line(margin + labelW + palletColW * i, tableY, margin + labelW + palletColW * i, tableY + rowH * 3);
  }
  doc.line(margin + labelW + palletColW * 3, tableY, margin + labelW + palletColW * 3, tableY + rowH * 3);

  // Data rows
  doc.setFont('helvetica', 'normal');
  const rowCounts = [palletCounts, returnCounts || null];
  for (let r = 0; r < rowLabels.length; r++) {
    const ry = tableY + rowH * (r + 1);
    doc.rect(margin, ry, tableW, rowH);
    doc.setFontSize(7);
    doc.setTextColor(...GRAY);
    doc.text(rowLabels[r], margin + 2, ry + 5);

    const counts = rowCounts[r];
    if (counts && (counts.eu || counts.half || counts.quarter)) {
      doc.setTextColor(...BLACK);
      doc.setFont('helvetica', 'bold');
      doc.text(String(counts.eu || 0), margin + labelW + palletColW * 0.5, ry + 5, { align: 'center' });
      doc.text(String(counts.half || 0), margin + labelW + palletColW * 1.5, ry + 5, { align: 'center' });
      doc.text(String(counts.quarter || 0), margin + labelW + palletColW * 2.5, ry + 5, { align: 'center' });
      doc.setFont('helvetica', 'normal');
    } else {
      doc.setTextColor(...LIGHT_GRAY);
      doc.text('0', margin + labelW + palletColW * 0.5, ry + 5, { align: 'center' });
      doc.text('0', margin + labelW + palletColW * 1.5, ry + 5, { align: 'center' });
      doc.text('0', margin + labelW + palletColW * 2.5, ry + 5, { align: 'center' });
    }
  }

  // Kommentar line
  const commentY = tableY + rowH * 3 + 3;
  doc.setFontSize(7);
  doc.setTextColor(...GRAY);
  doc.text('Kommentar:', margin, commentY);
  doc.line(margin + 20, commentY + 1, margin + tableW, commentY + 1);

  // "Ingen personer tilstede" checkbox area
  const checkX = margin + contentW - 40;
  doc.rect(checkX - 4, commentY - 4, 3, 3);
  doc.text('Ingen personer tilstede', checkX, commentY);

  // Signature box
  const sigY = commentY + 6;
  doc.setDrawColor(...LINE_COLOR);
  doc.rect(margin, sigY, contentW, 12);

  doc.setFontSize(7);
  doc.setTextColor(...GRAY);
  doc.text(signatureLabel, margin + 3, sigY + 7);

  // Dato / Kl. fields
  doc.text('Dato:', margin + contentW * 0.55, sigY + 7);
  doc.text('Kl.:', margin + contentW * 0.78, sigY + 7);
}

// ──────────────────────────────────────────
// BARCODE (visual approximation)
// ──────────────────────────────────────────
function drawBarcode(doc, text, x, y, width, height) {
  const seed = text.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const numBars = Math.min(text.length * 8, 60);
  const barW = width / numBars;
  let pos = x;

  for (let i = 0; i < numBars; i++) {
    const isBar = ((i * 7 + seed) % 3) !== 0;
    if (isBar) {
      const bw = barW * (0.5 + ((i * 3 + seed) % 3) * 0.3);
      doc.setFillColor(0, 0, 0);
      doc.rect(pos, y, bw, height, 'F');
      pos += bw;
    } else {
      pos += barW * (0.3 + (i % 2) * 0.4);
    }
    if (pos > x + width) break;
  }

}
